import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { format, isWeekend, isBefore, startOfDay } from 'date-fns';
import { de as deLocale } from 'date-fns/locale';
import { ArrowLeft, CalendarIcon, Clock, Save, Users, Video, Check, X, Eye, EyeOff, Mail } from 'lucide-react';
import { API } from '@/lib/api';

const WEEKDAY_SLOTS = ['10:00','10:15','10:30','10:45','11:00','11:15','11:30','11:45','14:00','14:15','14:30','14:45','15:00','15:15','15:30','15:45'];
const WEEKEND_SLOTS = ['10:00','10:30','11:00','11:30','14:00','14:30','15:00','15:30'];

export default function AdminCalendarPage() {
  const { language } = useLanguage();
  const { user, getHeaders } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';

  const [selectedDate, setSelectedDate] = useState(null);
  const [activeSlots, setActiveSlots] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => { fetchBookings(); }, []);
  useEffect(() => { if (selectedDate) fetchAvailability(format(selectedDate, 'yyyy-MM-dd')); }, [selectedDate]);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: getHeaders() });
      setAllBookings(res.data || []);
    } catch {}
  };

  const fetchAvailability = async (date) => {
    try {
      const res = await axios.get(`${API}/admin/availability?date=${date}`, { headers: getHeaders() });
      setActiveSlots(res.data?.slots || []);
    } catch { setActiveSlots([]); }
  };

  const toggleSlot = (slot) => {
    setActiveSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot].sort());
  };

  const selectAllSlots = () => {
    const base = selectedDate && isWeekend(selectedDate) ? WEEKEND_SLOTS : WEEKDAY_SLOTS;
    setActiveSlots([...base]);
  };

  const clearAllSlots = () => setActiveSlots([]);

  const handleSave = async () => {
    if (!selectedDate) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/availability`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        slots: activeSlots,
      }, { headers: getHeaders() });
      toast.success(de ? `${activeSlots.length} Slots für ${format(selectedDate, 'dd.MM.')} gespeichert` : `${activeSlots.length} slots saved for ${format(selectedDate, 'MM/dd')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setSaving(false);
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await axios.post(`${API}/admin/send-reminders`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
    } catch { toast.error('Error'); }
    setSendingReminders(false);
  };

  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const isWE = selectedDate ? isWeekend(selectedDate) : false;
  const dayBookings = allBookings.filter(b => b.date === dateStr);
  const upcomingBookings = allBookings.filter(b => !isBefore(new Date(b.date + 'T23:59:59'), new Date())).slice(0, 30);
  const availableSlots = isWE ? WEEKEND_SLOTS : WEEKDAY_SLOTS;

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-foreground/40">{de ? 'Nur für Admins' : 'Admin only'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-5xl" data-testid="admin-calendar-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Admin-Kalender' : 'Admin Calendar'}</h1>
            <p className="font-body text-sm text-foreground/50">{de ? 'Slots freigeben, sperren & Termine verwalten' : 'Release slots, block & manage appointments'}</p>
          </div>
        </div>
        <Button onClick={handleSendReminders} disabled={sendingReminders} variant="outline" size="sm" className="rounded-full" data-testid="send-reminders-btn">
          <Mail size={14} className="mr-1" /> {sendingReminders ? '...' : de ? 'Reminder senden' : 'Send reminders'}
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="admin-calendar">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <CalendarIcon size={16} className="text-terracotta" /> {de ? 'Datum' : 'Date'}
          </h3>
          <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate}
            locale={de ? deLocale : undefined}
            disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
            className="rounded-xl" />
          <p className="font-body text-[10px] text-foreground/30 mt-2">{de ? 'Auch Sa/So wählbar – Slots manuell freigeben' : 'Sat/Sun selectable – release slots manually'}</p>
        </Card>

        {/* Slots Management */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="admin-slots">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <Clock size={16} className="text-terracotta" />
            {de ? 'Slots verwalten' : 'Manage Slots'}
            {isWE && <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px] rounded-full">Wochenende</Badge>}
          </h3>
          {!selectedDate ? (
            <p className="font-body text-sm text-foreground/40 py-8 text-center">{de ? 'Datum wählen' : 'Choose date'}</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="font-body text-xs text-foreground/40">
                  {format(selectedDate, de ? 'EEEE, dd. MMMM' : 'EEEE, MMMM dd', { locale: de ? deLocale : undefined })}
                </p>
                <div className="flex gap-1">
                  <button onClick={selectAllSlots} className="px-2 py-1 rounded-md text-[10px] font-body font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors" data-testid="select-all-slots">
                    <Eye size={10} className="inline mr-1" />{de ? 'Alle freigeben' : 'Enable all'}
                  </button>
                  <button onClick={clearAllSlots} className="px-2 py-1 rounded-md text-[10px] font-body font-medium bg-red-50 text-red-500 hover:bg-red-100 transition-colors" data-testid="clear-all-slots">
                    <EyeOff size={10} className="inline mr-1" />{de ? 'Alle sperren' : 'Block all'}
                  </button>
                </div>
              </div>

              {/* Vormittag */}
              <p className="font-body text-[10px] font-semibold text-foreground/40 mb-1.5">{de ? 'Vormittag' : 'Morning'}</p>
              <div className="grid grid-cols-4 gap-1.5 mb-3">
                {availableSlots.filter(s => parseInt(s) < 13).map(slot => {
                  const isBooked = dayBookings.some(b => b.time === slot);
                  const isActive = activeSlots.includes(slot);
                  return (
                    <button key={slot} onClick={() => !isBooked && toggleSlot(slot)}
                      className={`p-2 rounded-lg font-body text-[11px] font-medium transition-all border ${isBooked ? 'bg-red-100 text-red-500 border-red-200 cursor-not-allowed line-through' : isActive ? 'bg-terracotta text-white border-terracotta' : 'bg-warmgray/40 text-foreground/30 border-border/30 hover:border-terracotta/50'}`}
                      data-testid={`admin-slot-${slot}`}>
                      {slot}
                    </button>
                  );
                })}
              </div>

              {/* Nachmittag */}
              <p className="font-body text-[10px] font-semibold text-foreground/40 mb-1.5">{de ? 'Nachmittag' : 'Afternoon'}</p>
              <div className="grid grid-cols-4 gap-1.5 mb-3">
                {availableSlots.filter(s => parseInt(s) >= 13).map(slot => {
                  const isBooked = dayBookings.some(b => b.time === slot);
                  const isActive = activeSlots.includes(slot);
                  return (
                    <button key={slot} onClick={() => !isBooked && toggleSlot(slot)}
                      className={`p-2 rounded-lg font-body text-[11px] font-medium transition-all border ${isBooked ? 'bg-red-100 text-red-500 border-red-200 cursor-not-allowed line-through' : isActive ? 'bg-terracotta text-white border-terracotta' : 'bg-warmgray/40 text-foreground/30 border-border/30 hover:border-terracotta/50'}`}
                      data-testid={`admin-slot-${slot}`}>
                      {slot}
                    </button>
                  );
                })}
              </div>

              {/* Legende */}
              <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-terracotta" /><span className="font-body text-[9px] text-foreground/40">{de ? 'Sichtbar/Frei' : 'Visible/Free'}</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-warmgray/40 border border-border/30" /><span className="font-body text-[9px] text-foreground/40">{de ? 'Gesperrt' : 'Blocked'}</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-100 border border-red-200" /><span className="font-body text-[9px] text-foreground/40">{de ? 'Gebucht' : 'Booked'}</span></div>
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-5" data-testid="admin-save-slots">
                  <Save size={14} className="mr-1" /> {saving ? '...' : de ? 'Speichern' : 'Save'}
                </Button>
                <span className="font-body text-xs text-foreground/40">
                  {activeSlots.length} {de ? 'sichtbar' : 'visible'} / {availableSlots.length} {de ? 'möglich' : 'possible'}
                </span>
              </div>

              {/* Gebuchte Termine für diesen Tag */}
              {dayBookings.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border/30">
                  <p className="font-body text-xs font-semibold text-foreground/60 mb-2">{de ? 'Gebuchte Termine:' : 'Booked:'}</p>
                  {dayBookings.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5">
                      <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">{b.time}</Badge>
                      <span className="font-body text-xs text-foreground/70 font-medium">{b.user_name}</span>
                      <span className="font-body text-[10px] text-foreground/40">{b.user_email}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>

        {/* All Upcoming Bookings */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="admin-all-bookings">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <Users size={16} className="text-terracotta" /> {de ? 'Alle Termine' : 'All Appointments'}
            <Badge className="bg-warmgray/50 text-foreground/40 border-0 text-[10px] rounded-full">{upcomingBookings.length}</Badge>
          </h3>
          {upcomingBookings.length === 0 ? (
            <p className="font-body text-sm text-foreground/40 py-4 text-center">{de ? 'Keine Termine' : 'No appointments'}</p>
          ) : (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
              {upcomingBookings.map((b, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-warmgray/30 hover:bg-warmgray/50 transition-colors" data-testid={`admin-booking-${i}`}>
                  <Video size={14} className="text-terracotta shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-xs font-medium">{b.user_name}</p>
                    <p className="font-body text-[10px] text-foreground/40">{b.user_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-body text-xs font-medium">{b.date}</p>
                    <p className="font-body text-[10px] text-terracotta font-semibold">{b.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
